import React, { useState, useRef, useEffect } from 'react';
import { attachAutocomplete } from '../../../utils/googlePlaces';
import './AddPropertyForm.css';
import { toast } from 'react-toastify';
import { FaTimes, FaCloudUploadAlt, FaMapMarkerAlt } from 'react-icons/fa';
import { getApiEndpoint } from '../../../config/api';

export default function AddPropertyForm({ onClose, onAddProperty }) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [coords, setCoords] = useState({ latitude: null, longitude: null });
  const addressRef = useRef(null);

  // Google address lookup on the address field. Picking a suggestion also captures
  // the coordinates, so the property is placed on the map on creation instead of
  // waiting for the geocode backfill. Falls back to plain typing if unavailable.
  useEffect(() => {
    let cleanup;
    attachAutocomplete(addressRef.current, (p) => {
      if (p.address) setAddress(p.address);
      setCoords({ latitude: p.latitude, longitude: p.longitude });
    }).then((fn) => { if (typeof fn === 'function') cleanup = fn; });
    return () => { if (cleanup) cleanup(); };
  }, []);
  const [selectedImage, setSelectedImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB');
      return;
    }
    try {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(file);
      const processed = await processImage(file);
      setSelectedImage(processed);
    } catch {
      toast.error('Error processing image');
    }
  };

  const removeImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedImage(null);
    setPreview(null);
  };

  const processImage = (file) =>
    new Promise((resolve) => {
      const img = new Image();
      const reader = new FileReader();
      reader.onload = (e) => {
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const tw = 800;
          const th = (tw * 11) / 16;
          canvas.width = tw;
          canvas.height = th;
          const tr = tw / th;
          const ir = img.width / img.height;
          let sx = 0, sy = 0, sw = img.width, sh = img.height;
          if (ir > tr) { sw = img.height * tr; sx = (img.width - sw) / 2; }
          else { sh = img.width / tr; sy = (img.height - sh) / 2; }
          ctx.fillStyle = '#f5f5f5';
          ctx.fillRect(0, 0, tw, th);
          ctx.drawImage(img, sx, sy, sw, sh, 0, 0, tw, th);
          canvas.toBlob((blob) => {
            resolve(new File([blob], 'property-image.jpg', { type: 'image/jpeg', lastModified: Date.now() }));
          }, 'image/jpeg', 0.9);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });

  const canSubmit = name.trim().length > 0 && address.trim().length >= 5;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Please enter a property name'); return; }
    if (address.trim().length < 5) { toast.error('Please enter a property address'); return; }

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('name', name.trim());
      fd.append('address', address.trim());
      if (coords.latitude != null && coords.longitude != null) {
        fd.append('latitude', coords.latitude);
        fd.append('longitude', coords.longitude);
      }
      if (selectedImage) fd.append('image', selectedImage);

      const res = await fetch(getApiEndpoint('/api/properties'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to add property');
      }
      const data = await res.json();
      onAddProperty(data);
      onClose();
      toast.success('Property added successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to add property');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-property-overlay" onClick={onClose}>
      <div className="add-property-form" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="close-button" onClick={onClose}>
          <FaTimes />
        </button>

        <h2>Add New Property</h2>
        <p className="form-subtitle">Enter property details below</p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="prop-name">Property Name</label>
            <input
              type="text"
              id="prop-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter property name"
              required
              disabled={loading}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="prop-address">Property Address</label>
            <div className="address-input-container">
              <FaMapMarkerAlt className="address-icon" />
              <input
                type="text"
                id="prop-address"
                ref={addressRef}
                value={address}
                onChange={(e) => { setAddress(e.target.value); setCoords({ latitude: null, longitude: null }); }}
                onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                placeholder="Start typing an address…"
                autoComplete="off"
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group form-group-upload">
            <span className="field-label">Property Image <span className="optional-tag">Optional</span></span>
            <div
              className="upload-placeholder"
              role="button"
              tabIndex={0}
              onClick={() => !loading && !preview && fileInputRef.current?.click()}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); !loading && !preview && fileInputRef.current?.click(); } }}
            >
              {preview ? (
                <div className="preview-wrapper">
                  <img src={preview} alt="Preview" className="image-preview" />
                  <button type="button" className="remove-preview" onClick={removeImage} title="Remove image">
                    <FaTimes />
                  </button>
                </div>
              ) : (
                <>
                  <FaCloudUploadAlt className="upload-icon" />
                  <span className="upload-main-text">Click to upload image</span>
                  <span className="upload-hint">JPG, PNG or GIF – max 5 MB</span>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleImageChange}
              accept="image/jpeg,image/png,image/gif"
              className="file-input"
              disabled={loading}
            />
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="submit-button" disabled={loading || !canSubmit}>
              {loading ? 'Adding…' : 'Add Property'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
